import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import { openModalAction } from '../../../actions/modal';
import { actionsAdvancedFilters } from '../../../components/AdvancedFilters/actions';
import AdvancedFilters, { Filter } from '../../../components/AdvancedFilters/AdvancedFilters';
import AdvancedFiltersBar from '../../../components/AdvancedFilters/AdvancedFiltersBar';
import SimpleModal from '../../../containers/Modal/SimpleModal';
import { PageHeader } from '../../../components/PageHeader';
import { Button } from '../../../components/PageHeader/Button';
import { TextInput } from '../../../components/PageHeader/TextInput';
import Select from '../../../components/Select';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import actions from './redux/actions';
import {
  buildCheckboxFilterDefinition,
  getAdvancedFilterValues,
  HEALTH_DATE_FILTER_OPTIONS,
  isCustomDateRangeOption,
  resolveDateRange,
} from './shared';

import './MessagesOverview.scss';

const DEFAULT_LIMIT = 200;
const DEFAULT_DATE_FILTER = HEALTH_DATE_FILTER_OPTIONS[1];
const MESSAGE_DETAIL_FILTER_GROUPS = {
  org: 'healthMessageDetailOrg',
  status: 'healthMessageDetailStatus',
  type: 'healthMessageDetailType',
};

function TruncatedCell({ value }) {
  const text = value ? String(value) : '-';
  return (
    <div className="health-event-details-table__truncated" title={text}>
      {text}
    </div>
  );
}

TruncatedCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function renderDateTimeCell(info) {
  const value = info.getValue();
  return value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '-';
}

function renderValueOrDashCell(info) {
  return info.getValue() || '-';
}

function renderTruncatedCell(info) {
  return <TruncatedCell value={info.getValue()} />;
}

function shouldShowPendingRecheck(status) {
  return status === 'queued' || status === 'queuing';
}

function toCsv(values) {
  return values?.length ? values.join(',') : undefined;
}

function buildFilterDefinitions(bundle) {
  return {
    ...buildCheckboxFilterDefinition(
      MESSAGE_DETAIL_FILTER_GROUPS.org,
      'Organization',
      (bundle.organizations || []).map(org => ({ value: String(org.org_id), label: org.org_name })),
    ),
    ...buildCheckboxFilterDefinition(
      MESSAGE_DETAIL_FILTER_GROUPS.status,
      'Status',
      (bundle.statuses || []).map(status => ({ value: status, label: status })),
    ),
    ...buildCheckboxFilterDefinition(
      MESSAGE_DETAIL_FILTER_GROUPS.type,
      'Message type',
      (bundle.message_types || []).map(type => ({ value: type, label: type })),
    ),
  };
}

function MessageDetailsTab({
  clearAllAdvancedFilters,
  drilldownRequest,
  filters,
  getMessageDetails,
  getMessageDetailsFormBundle,
  onBack,
  openDateRangeSelectionModal,
  recheckPendingMessage,
  updateAdvancedFilterState,
}) {
  const [bundle, setBundle] = React.useState({ organizations: [], statuses: [], message_types: [] });
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [rows, setRows] = React.useState([]);
  const [pagination, setPagination] = React.useState({ offset: 0, limit: DEFAULT_LIMIT, totalRecords: 0 });
  const [dateFilterValue, setDateFilterValue] = React.useState(DEFAULT_DATE_FILTER.value);
  const [dateRange, setDateRange] = React.useState(resolveDateRange(DEFAULT_DATE_FILTER));
  const [searchQuery, setSearchQuery] = React.useState('');
  const [pendingRecheckState, setPendingRecheckState] = React.useState(null);
  const pageRequest = React.useRef({ offset: 0, limit: DEFAULT_LIMIT });

  React.useEffect(() => {
    getMessageDetailsFormBundle().then(resp => {
      setBundle(resp?.response || { organizations: [], statuses: [], message_types: [] });
    });
  }, [getMessageDetailsFormBundle]);

  const selectedOrgIds = React.useMemo(
    () => getAdvancedFilterValues(filters, MESSAGE_DETAIL_FILTER_GROUPS.org, value => Number(value)),
    [filters],
  );
  const selectedStatuses = React.useMemo(
    () => getAdvancedFilterValues(filters, MESSAGE_DETAIL_FILTER_GROUPS.status),
    [filters],
  );
  const selectedMessageTypes = React.useMemo(
    () => getAdvancedFilterValues(filters, MESSAGE_DETAIL_FILTER_GROUPS.type),
    [filters],
  );

  const loadData = React.useCallback(() => {
    setIsLoading(true);
    setError('');

    const params = {
      offset: pageRequest.current.offset,
      limit: pageRequest.current.limit,
      org_ids: toCsv(selectedOrgIds),
      statuses: toCsv(selectedStatuses),
      message_types: toCsv(selectedMessageTypes),
      search: searchQuery.trim() || undefined,
    };

    const requestRange = dateFilterValue === 'custom'
      ? dateRange
      : resolveDateRange({ value: dateFilterValue });
    if (requestRange.start) {
      params.start_at = requestRange.start.clone().utc().toISOString();
    }
    if (requestRange.end) {
      params.end_at = requestRange.end.clone().utc().toISOString();
    }

    getMessageDetails(params)
      .then(resp => {
        const response = resp?.response || {};
        setRows(response.data || []);
        setPagination({
          offset: response.offset || 0,
          limit: response.limit || pageRequest.current.limit,
          totalRecords: response.totalRecords || 0,
        });
        setIsLoading(false);
      })
      .catch(() => {
        setError('Unable to load message details.');
        setIsLoading(false);
      });
  }, [dateFilterValue, dateRange.end, dateRange.start, getMessageDetails, searchQuery, selectedMessageTypes, selectedOrgIds, selectedStatuses]);

  React.useEffect(() => {
    pageRequest.current.offset = 0;
    loadData();
  }, [loadData]);

  React.useEffect(() => {
    if (!drilldownRequest?.key) {
      return;
    }

    clearAllAdvancedFilters();

    (drilldownRequest.org_ids || []).forEach(value => {
      updateAdvancedFilterState({ group: MESSAGE_DETAIL_FILTER_GROUPS.org, value: String(value), state: true });
    });
    (drilldownRequest.statuses || []).forEach(value => {
      updateAdvancedFilterState({ group: MESSAGE_DETAIL_FILTER_GROUPS.status, value, state: true });
    });
    (drilldownRequest.message_types || []).forEach(value => {
      updateAdvancedFilterState({ group: MESSAGE_DETAIL_FILTER_GROUPS.type, value, state: true });
    });

    setDateFilterValue('custom');
    setDateRange({
      start: drilldownRequest.start_at ? moment(drilldownRequest.start_at) : undefined,
      end: drilldownRequest.end_at ? moment(drilldownRequest.end_at) : undefined,
    });
    setSearchQuery('');
  }, [clearAllAdvancedFilters, drilldownRequest, updateAdvancedFilterState]);

  const filterDefinitions = React.useMemo(() => buildFilterDefinitions(bundle), [bundle]);

  const onDateRangeChanged = React.useCallback(option => {
    if (isCustomDateRangeOption(option)) {
      const onDateSubmited = dates => {
        setDateFilterValue('custom');
        setDateRange({
          start: dates.startDate ? moment(dates.startDate * 1000) : undefined,
          end: dates.endDate ? moment(dates.endDate * 1000) : undefined,
        });
      };
      openDateRangeSelectionModal({ actionOnDone: onDateSubmited });
      return;
    }

    setDateFilterValue(option?.value || DEFAULT_DATE_FILTER.value);
    setDateRange(resolveDateRange(option || DEFAULT_DATE_FILTER));
  }, [openDateRangeSelectionModal]);

  const onPendingRecheckClick = React.useCallback(row => {
    setPendingRecheckState({
      row,
      isLoading: true,
      result: null,
      error: '',
    });

    recheckPendingMessage(row.id)
      .then(resp => {
        const result = resp?.response || {};
        setPendingRecheckState({
          row,
          isLoading: false,
          result,
          error: '',
        });
        if (result?.updated) {
          loadData();
        }
      })
      .catch(() => {
        setPendingRecheckState({
          row,
          isLoading: false,
          result: null,
          error: 'Unable to recheck message status.',
        });
      });
  }, [loadData, recheckPendingMessage]);

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('created_at', {
        header: 'Created at',
        cell: renderDateTimeCell,
        size: 180,
      }),
      columnHelper.accessor('message_type', {
        header: 'Message type',
        cell: renderValueOrDashCell,
        size: 220,
      }),
      columnHelper.accessor('patient_id', {
        header: 'Patient ID',
        cell: renderValueOrDashCell,
        size: 110,
      }),
      columnHelper.accessor('phone_number', {
        header: 'Phone number',
        cell: renderValueOrDashCell,
        size: 160,
      }),
      columnHelper.accessor('org_name', {
        header: 'Organization',
        cell: renderValueOrDashCell,
        size: 180,
      }),
      columnHelper.accessor('message_text', {
        header: 'Message text',
        cell: renderTruncatedCell,
        size: 420,
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: renderValueOrDashCell,
        size: 140,
      }),
      columnHelper.accessor('vendor_message', {
        header: 'Vendor message',
        cell: renderTruncatedCell,
        size: 420,
      }),
    ],
    [],
  );

  const tableButtons = React.useMemo(() => [
    {
      icon: 'refresh',
      text: 'Recheck message status',
      visible: (_, row) => shouldShowPendingRecheck(row.status),
      onClick: (_, row) => onPendingRecheckClick(row),
    },
  ], [onPendingRecheckClick]);

  return (
    <>
      <PageHeader
        onBackClick={onBack}
        left="Message details"
        right={(
          <div className="health-messages__table-header-actions">
            <Select
              id="health-message-details-date-range"
              data={HEALTH_DATE_FILTER_OPTIONS}
              value={dateFilterValue}
              onChange={onDateRangeChanged}
            />
            <TextInput
              class="search"
              placeholder="Search phone or patient ID"
              value={searchQuery}
              onChange={event => setSearchQuery(event.target.value)}
            />
            <AdvancedFilters>
              <Filter definition={filterDefinitions[MESSAGE_DETAIL_FILTER_GROUPS.org]} />
              <Filter definition={filterDefinitions[MESSAGE_DETAIL_FILTER_GROUPS.status]} />
              <Filter definition={filterDefinitions[MESSAGE_DETAIL_FILTER_GROUPS.type]} />
            </AdvancedFilters>
            <Button class="health-refresh-button" onClick={loadData} title="Refresh" />
          </div>
        )}
      />
      <AdvancedFiltersBar customFilters={filterDefinitions} />

      {error ? <div className="health-messages-error">{error}</div> : null}

      <Table3
        name="health-message-details"
        data={rows}
        columns={columns}
        buttons={tableButtons}
        isLoading={isLoading}
        pagination={pagination}
        onOffsetChange={offset => {
          pageRequest.current.offset = offset;
          loadData();
        }}
        onPageSizeChange={limit => {
          pageRequest.current.limit = limit;
          pageRequest.current.offset = 0;
          loadData();
        }}
      />

      {pendingRecheckState ? (
        <SimpleModal
          name="health-message-details-recheck-pending"
          title={`Recheck message status ${pendingRecheckState.row?.id || ''}`}
          onCancel={() => setPendingRecheckState(null)}
          onSubmit={() => {}}
          onSubmitDisabled
          customButtons={(
            <div className="buttons" key="button-bar">
              <div className="bottom-button-container" />
              <div className="bottom-button-container">
                <button className="brand-blue" type="button" onClick={() => setPendingRecheckState(null)}>
                  Close
                </button>
              </div>
            </div>
          )}
          content={(
            <div className="health-message-details__recheck-modal">
              {pendingRecheckState.isLoading ? (
                <div>Checking pending message status...</div>
              ) : null}

              {!pendingRecheckState.isLoading && pendingRecheckState.error ? (
                <div className="health-messages-error">{pendingRecheckState.error}</div>
              ) : null}

              {!pendingRecheckState.isLoading && !pendingRecheckState.error && pendingRecheckState.result ? (
                <>
                  <div className="health-message-details__recheck-summary">
                    <strong>{pendingRecheckState.result.summary || 'Check completed.'}</strong>
                  </div>
                  <div className="health-message-details__recheck-grid">
                    <div><strong>Initial status:</strong> {pendingRecheckState.result.initial_status || '-'}</div>
                    <div><strong>Final status:</strong> {pendingRecheckState.result.final_status || '-'}</div>
                    <div><strong>Action:</strong> {pendingRecheckState.result.action || '-'}</div>
                    <div><strong>Updated:</strong> {pendingRecheckState.result.updated ? 'yes' : 'no'}</div>
                  </div>

                  {pendingRecheckState.result.check_error ? (
                    <div className="health-message-details__recheck-error">
                      <strong>Check error:</strong> {pendingRecheckState.result.check_error}
                    </div>
                  ) : null}

                  {pendingRecheckState.result.twilio ? (
                    <div className="health-message-details__recheck-section">
                      <div className="health-message-details__recheck-section-title">Twilio</div>
                      <div className="health-message-details__recheck-grid">
                        <div><strong>Message SID:</strong> {pendingRecheckState.result.twilio.message_sid || '-'}</div>
                        <div><strong>Status:</strong> {pendingRecheckState.result.twilio.status || '-'}</div>
                        <div><strong>Error code:</strong> {pendingRecheckState.result.twilio.error_code ?? '-'}</div>
                        <div><strong>Error message:</strong> {pendingRecheckState.result.twilio.error_message || '-'}</div>
                        <div><strong>Matched by:</strong> {pendingRecheckState.result.twilio.matched_by || '-'}</div>
                        <div><strong>Mapped fail reason:</strong> {pendingRecheckState.result.twilio.mapped_fail_reason || '-'}</div>
                      </div>
                    </div>
                  ) : null}

                  {pendingRecheckState.result.steps?.length ? (
                    <div className="health-message-details__recheck-section">
                      <div className="health-message-details__recheck-section-title">Steps</div>
                      <ul className="health-message-details__recheck-steps">
                        {pendingRecheckState.result.steps.map(step => (
                          <li key={step}>{step}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          )}
        />
      ) : null}
    </>
  );
}

MessageDetailsTab.propTypes = {
  clearAllAdvancedFilters: PropTypes.func.isRequired,
  drilldownRequest: PropTypes.shape({
    key: PropTypes.string,
    start_at: PropTypes.string,
    end_at: PropTypes.string,
    org_ids: PropTypes.arrayOf(PropTypes.number),
    statuses: PropTypes.arrayOf(PropTypes.string),
    message_types: PropTypes.arrayOf(PropTypes.string),
  }),
  filters: PropTypes.array.isRequired,
  getMessageDetails: PropTypes.func.isRequired,
  getMessageDetailsFormBundle: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  openDateRangeSelectionModal: PropTypes.func.isRequired,
  recheckPendingMessage: PropTypes.func.isRequired,
  updateAdvancedFilterState: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  filters: state.entities.advancedFilters.filters.items,
});

const mapDispatchToProps = dispatch => ({
  clearAllAdvancedFilters: () => dispatch(actionsAdvancedFilters.clearAll()),
  getMessageDetails: params => dispatch(actions.getMessageDetails(params)),
  getMessageDetailsFormBundle: () => dispatch(actions.getMessageDetailsFormBundle()),
  recheckPendingMessage: id => dispatch(actions.recheckPendingMessage(id)),
  openDateRangeSelectionModal: data => dispatch(openModalAction('data-range-selection-modalion', data)),
  updateAdvancedFilterState: data => dispatch(actionsAdvancedFilters.updateFilterState(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MessageDetailsTab);
